/**
 * The density field, evaluated on the GPU.
 *
 * The field is a sum over every item at every grid node:
 *
 * ```
 *   d(p) = sum_k  w_k * exp( -||p - p_k||^2 / h^2 )
 * ```
 *
 * At the 2000-item cap and VOSviewer's 250x250 grid that is 125 million
 * exponentials.  A fragment shader does them all in parallel in a few
 * milliseconds; this is the one place in the renderer where WebGL clearly
 * earns its keep, and it is why the density views can be recoloured, retuned
 * and re-kernelled interactively instead of only at load time.
 *
 * Both fields come out of a **single pass** using WebGL2's multiple render
 * targets:
 *
 * * attachment 0 — `d` over all items;
 * * attachment 1 — `(sum w*k*colour, sum w*k)` over items in large clusters,
 *   which is everything the cluster density view needs, because its hue is the
 *   density-weighted mean of the cluster colours and that is exactly
 *   `rgb / a` of this texel.
 *
 * The result is read back once per data change; maxima and colouring happen on
 * the CPU over 62 500 cells, which is nothing.  Pan and zoom never re-enter
 * this file.
 */

import type { Bounds } from '../geometry/viewport';

/** Width of the item data textures. Items are laid out row-major into them. */
const DATA_TEX_WIDTH = 256;

const VERTEX_SHADER = `#version 300 es
// A single triangle covering clip space: cheaper than a quad and with no
// diagonal seam where the two triangles of a quad meet.
void main() {
  vec2 p = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2);
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER = `#version 300 es
precision highp float;
precision highp int;
precision highp sampler2D;

uniform sampler2D uItems;    // rgba = (x, y, normalisedWeight, inLargeCluster)
uniform sampler2D uColors;   // rgb  = cluster colour, 0..1
uniform int   uCount;
uniform int   uTexWidth;
uniform float uGridSize;
uniform vec2  uExtentMin;
uniform vec2  uExtentSpan;
uniform float uInvH2;        // 1 / h^2
uniform float uLogThreshold; // ln(itemDensityThreshold)

layout(location = 0) out vec4 outItem;
layout(location = 1) out vec4 outCluster;

void main() {
  // Grid node i sits at extentMin + i / (gridSize - 1) * span, sampling both
  // endpoints of the extent, exactly as calcDensities() lays out its axes.
  vec2 node = (gl_FragCoord.xy - 0.5) / (uGridSize - 1.0);
  vec2 p = uExtentMin + node * uExtentSpan;

  float total = 0.0;
  vec4 cluster = vec4(0.0);

  for (int k = 0; k < uCount; ++k) {
    ivec2 texel = ivec2(k % uTexWidth, k / uTexWidth);
    vec4 item = texelFetch(uItems, texel, 0);
    vec2 delta = p - item.xy;
    // NO FACTOR OF 2 IN THE EXPONENT. h is a kernel width, not a standard
    // deviation: VOSviewer's calcDensities computes
    //   logItemDensity = -(dx*dx + dy*dy) / squaredKernelWidth
    // and dividing by 2*h^2 instead would widen every kernel by a factor of
    // sqrt(2), which shows up as a visibly blurrier, flatter map.
    float logDensity = -dot(delta, delta) * uInvH2;
    // VOSviewer stops an item contributing once its kernel falls below
    // ITEM_DENSITY_THRESHOLD; it does that with a bounding box, we do it with a
    // compare, and the arithmetic is the same.
    if (logDensity < uLogThreshold - log(item.z)) continue;
    float contribution = item.z * exp(logDensity);
    total += contribution;
    if (item.w > 0.5) {
      vec3 colour = texelFetch(uColors, texel, 0).rgb;
      cluster += vec4(colour * contribution, contribution);
    }
  }

  outItem = vec4(total, 0.0, 0.0, 1.0);
  outCluster = cluster;
}
`;

export interface DensityGrids {
  gridSize: number;
  /** `gridSize * gridSize` densities over all items, row-major from the extent's lower edge. */
  item: Float32Array;
  /** `gridSize * gridSize * 4`: weighted colour sum in rgb, cluster density in a. */
  cluster: Float32Array;
  itemMax: number;
  clusterMax: number;
}

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('could not create shader');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? 'unknown error';
    gl.deleteShader(shader);
    throw new Error(`density shader failed to compile: ${log}`);
  }
  return shader;
}

/**
 * A WebGL2 context and its compiled program, kept alive between recomputations.
 *
 * Construction throws when WebGL2 or float render targets are unavailable; the
 * caller catches that and falls back to the CPU implementation, which computes
 * the identical field.
 */
export class GpuDensity {
  private readonly canvas: HTMLCanvasElement;

  private readonly gl: WebGL2RenderingContext;

  private readonly program: WebGLProgram;

  private readonly framebuffer: WebGLFramebuffer;

  private itemTarget: WebGLTexture | null = null;

  private clusterTarget: WebGLTexture | null = null;

  private readonly itemsTex: WebGLTexture;

  private readonly colorsTex: WebGLTexture;

  private readonly vao: WebGLVertexArrayObject;

  private targetSize = 0;

  private destroyed = false;

  constructor() {
    this.canvas = document.createElement('canvas');
    const gl = this.canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      preserveDrawingBuffer: false,
    });
    if (!gl) throw new Error('WebGL2 is not available');
    // Rendering to a float texture is an extension even in WebGL2. Without it
    // the sum would have to be packed into 8-bit channels, which cannot hold a
    // density range of several orders of magnitude.
    if (!gl.getExtension('EXT_color_buffer_float')) {
      throw new Error('EXT_color_buffer_float is not available');
    }
    this.gl = gl;

    const vs = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    const program = gl.createProgram();
    if (!program) throw new Error('could not create program');
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program) ?? 'unknown error';
      gl.deleteProgram(program);
      throw new Error(`density program failed to link: ${log}`);
    }
    this.program = program;

    const fb = gl.createFramebuffer();
    const items = gl.createTexture();
    const colors = gl.createTexture();
    const vao = gl.createVertexArray();
    if (!fb || !items || !colors || !vao) throw new Error('could not allocate GL objects');
    this.framebuffer = fb;
    this.itemsTex = items;
    this.colorsTex = colors;
    this.vao = vao;
  }

  private ensureTargets(gridSize: number): void {
    const gl = this.gl;
    if (this.targetSize === gridSize && this.itemTarget && this.clusterTarget) return;
    if (this.itemTarget) gl.deleteTexture(this.itemTarget);
    if (this.clusterTarget) gl.deleteTexture(this.clusterTarget);
    const make = (): WebGLTexture => {
      const tex = gl.createTexture();
      if (!tex) throw new Error('could not allocate render target');
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, gridSize, gridSize, 0, gl.RGBA, gl.FLOAT, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      return tex;
    };
    this.itemTarget = make();
    this.clusterTarget = make();
    this.targetSize = gridSize;
  }

  private uploadItems(
    x: Float64Array,
    y: Float64Array,
    normWeight: Float64Array,
    inLargeCluster: Uint8Array,
    clusterRgb: Uint8Array,
  ): { count: number; rows: number } {
    const gl = this.gl;
    const count = x.length;
    const rows = Math.max(1, Math.ceil(count / DATA_TEX_WIDTH));
    const positions = new Float32Array(DATA_TEX_WIDTH * rows * 4);
    const colours = new Float32Array(DATA_TEX_WIDTH * rows * 4);
    for (let i = 0; i < count; i += 1) {
      positions[i * 4] = x[i] as number;
      positions[i * 4 + 1] = y[i] as number;
      // A zero weight would make log(item.z) in the shader -Infinity and drop
      // the item; that is the right answer, but keep it finite and tiny so the
      // comparison is well defined on every driver.
      positions[i * 4 + 2] = Math.max(normWeight[i] as number, 1e-12);
      positions[i * 4 + 3] = inLargeCluster[i] === 1 ? 1 : 0;
      colours[i * 4] = (clusterRgb[i * 3] as number) / 255;
      colours[i * 4 + 1] = (clusterRgb[i * 3 + 1] as number) / 255;
      colours[i * 4 + 2] = (clusterRgb[i * 3 + 2] as number) / 255;
      colours[i * 4 + 3] = 1;
    }
    const upload = (tex: WebGLTexture, data: Float32Array): void => {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, DATA_TEX_WIDTH, rows, 0, gl.RGBA, gl.FLOAT, data);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    };
    upload(this.itemsTex, positions);
    upload(this.colorsTex, colours);
    return { count, rows };
  }

  compute(
    gridSize: number,
    extent: Bounds,
    bandwidth: number,
    itemDensityThreshold: number,
    x: Float64Array,
    y: Float64Array,
    normWeight: Float64Array,
    inLargeCluster: Uint8Array,
    clusterRgb: Uint8Array,
  ): DensityGrids {
    if (this.destroyed) throw new Error('GpuDensity has been destroyed');
    const gl = this.gl;
    this.ensureTargets(gridSize);
    const { count } = this.uploadItems(x, y, normWeight, inLargeCluster, clusterRgb);

    this.canvas.width = gridSize;
    this.canvas.height = gridSize;

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      this.itemTarget,
      0,
    );
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT1,
      gl.TEXTURE_2D,
      this.clusterTarget,
      0,
    );
    gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
      throw new Error('density framebuffer is incomplete');
    }

    gl.viewport(0, 0, gridSize, gridSize);
    gl.disable(gl.BLEND);
    gl.disable(gl.DEPTH_TEST);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.itemsTex);
    gl.uniform1i(gl.getUniformLocation(this.program, 'uItems'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.colorsTex);
    gl.uniform1i(gl.getUniformLocation(this.program, 'uColors'), 1);

    gl.uniform1i(gl.getUniformLocation(this.program, 'uCount'), count);
    gl.uniform1i(gl.getUniformLocation(this.program, 'uTexWidth'), DATA_TEX_WIDTH);
    gl.uniform1f(gl.getUniformLocation(this.program, 'uGridSize'), gridSize);
    gl.uniform2f(gl.getUniformLocation(this.program, 'uExtentMin'), extent[0], extent[1]);
    gl.uniform2f(
      gl.getUniformLocation(this.program, 'uExtentSpan'),
      extent[2] - extent[0],
      extent[3] - extent[1],
    );
    const h2 = bandwidth * bandwidth;
    gl.uniform1f(gl.getUniformLocation(this.program, 'uInvH2'), h2 > 0 ? 1 / h2 : 0);
    gl.uniform1f(
      gl.getUniformLocation(this.program, 'uLogThreshold'),
      Math.log(Math.max(itemDensityThreshold, Number.MIN_VALUE)),
    );

    gl.drawArrays(gl.TRIANGLES, 0, 3);

    const cells = gridSize * gridSize;
    const itemRaw = new Float32Array(cells * 4);
    gl.readBuffer(gl.COLOR_ATTACHMENT0);
    gl.readPixels(0, 0, gridSize, gridSize, gl.RGBA, gl.FLOAT, itemRaw);
    const cluster = new Float32Array(cells * 4);
    gl.readBuffer(gl.COLOR_ATTACHMENT1);
    gl.readPixels(0, 0, gridSize, gridSize, gl.RGBA, gl.FLOAT, cluster);

    gl.bindVertexArray(null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    const item = new Float32Array(cells);
    let itemMax = 0;
    let clusterMax = 0;
    for (let i = 0; i < cells; i += 1) {
      const v = itemRaw[i * 4] as number;
      item[i] = v;
      if (v > itemMax) itemMax = v;
      const a = cluster[i * 4 + 3] as number;
      if (a > clusterMax) clusterMax = a;
    }

    return { gridSize, item, cluster, itemMax, clusterMax };
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    const gl = this.gl;
    gl.deleteFramebuffer(this.framebuffer);
    gl.deleteTexture(this.itemsTex);
    gl.deleteTexture(this.colorsTex);
    if (this.itemTarget) gl.deleteTexture(this.itemTarget);
    if (this.clusterTarget) gl.deleteTexture(this.clusterTarget);
    gl.deleteProgram(this.program);
    gl.deleteVertexArray(this.vao);
    // Free the backing drawing buffer promptly rather than waiting for the GC
    // to notice; browsers cap the number of live WebGL contexts per page.
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
}
